/**
 * "Learn with a sample" (W2-14 onboarding-wizard; DESIGN.md §3.1, §5.14): a short chart on the synthesised drum loop
 * (sampleAudio.ts) that shows every line type, additive layers and a UI mover. It is built ONLY through Core
 * command bodies (one transaction over `Defaults.emptyDoc`, core/doc/build.ts `buildDocWithCore`), with template-like motions written out as
 * explicit keyframes, so it is exactly what a user could have made in the editor and passes `checkInvariants`.
 *
 * 120 BPM, 16 bars. Lines (export order):
 *  - Main (normal): notes in all four sections; slides left and right (bars 5-7, the "swing" template), spins 360
 *    degrees over one bar (bar 9), wobbles through a second, additive rotation layer (bars 13-14) and fades out;
 *  - Partner (normal, child of Main): fades in at bar 4 and takes the answering notes of the groove section;
 *  - Title (text): types its first words on, switches to the second ones, changes colour, fades out;
 *  - Picture (image): fades in, turns slowly, fades out;
 *  - Spinner (GIF, playing automatically): fades in and bobs;
 *  - Brush (paint): appears in the last section and paints a zigzag;
 *  - Score mover (UI mover of the score display): nudges the score aside at the end.
 * Media: the WAV song, a 16:9 illustration, the image line's PNG and the GIF, all generated (sampleImages.ts).
 */

import { Core } from '../../core/commands';
import { catalogVariant } from '../../core/ease/catalog';
import { HOLD, LINEAR } from '../../core/ease/index';
import type { Ease, EaseFamily, EaseVariant } from '../../core/ease/types';
import { Defaults } from '../../core/model/defaults';
import type {
  AssetId,
  AssetMeta,
  AssetRole,
  ChartDoc,
  KeyId,
  LineId,
  LineProp,
  NoteKind,
  TrackId,
  TrackValue,
} from '../../core/model/types';
import type { NoteDraft } from '../../core/commands/types';
import type { TxBuilder } from '../../core/ops/types';
import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import { newRpeId } from '../../core/util/ids';
import type { RGB } from '../../core/util/types';
import { assetMetaOf, sniffMedia } from '../../formats/package/sniff';
import { buildDocWithCore } from '../../core/doc/build';
import { sampleSongWav, SAMPLE_BPM, type OfflineContextFactory } from './sampleAudio';
import { sampleGif, sampleIllustration, sampleTexture } from './sampleImages';

/** Texts of the sample in the user's language. */
export interface SampleText {
  readonly title: string;
  readonly intro: string;
  readonly credit: string;
  readonly lines: {
    readonly main: string;
    readonly partner: string;
    readonly title: string;
    readonly picture: string;
    readonly spinner: string;
    readonly brush: string;
    readonly score: string;
  };
  /** Words shown by the text line, one after the other. */
  readonly words: readonly [first: string, second: string];
}

export interface SampleAsset {
  readonly id: AssetId;
  readonly bytes: Uint8Array;
  readonly mime: string;
  readonly role: AssetRole;
  readonly meta: AssetMeta;
  readonly path: string;
}

export interface SampleProject {
  readonly doc: ChartDoc;
  readonly assets: readonly SampleAsset[];
  readonly durationSec: number;
}

/** Package paths of the sample's files (never `illustration.png`: that path marks the wizard's placeholder). */
export const SAMPLE_PATHS = {
  music: 'sample-song.wav',
  illustration: 'sample-cover.png',
  texture: 'sample-picture.png',
  gif: 'sample-spinner.gif',
} as const;

// ---------------------------------------------------------------------------------------------------------------
// Keyframe helpers (each a sequence of Core commands)
// ---------------------------------------------------------------------------------------------------------------

/** Beat from a decimal with quarter / eighth / sixteenth precision (exact rational). */
export function beat(x: number): Beat {
  const w = Math.floor(x);
  return Beats.make(w, Math.round((x - w) * 16), 16);
}

function tween(family: EaseFamily, variant: EaseVariant): Ease {
  const entry = catalogVariant(family, variant);
  if (entry === null) throw new Error(`no RPE easing for ${family} ${variant}`);
  return { kind: 'tween', id: entry.easingType, left: 0, right: 1 };
}

const E = {
  hold: HOLD,
  linear: LINEAR,
  outSine: tween('sine', 'out'),
  inSine: tween('sine', 'in'),
  inOutSine: tween('sine', 'inout'),
  outQuad: tween('quad', 'out'),
  inQuad: tween('quad', 'in'),
  inOutCubic: tween('cubic', 'inout'),
  outBack: tween('back', 'out'),
} as const;

interface Point {
  readonly t: number;
  readonly v: TrackValue;
  /** Curve of the segment that starts here (default Linear). */
  readonly ease?: Ease;
}

function keyAt(tx: TxBuilder, track: TrackId, t: Beat): KeyId | undefined {
  for (const id of tx.idx.keysByTrack(track)) {
    const k = tx.doc.keys.get(id);
    if (k !== undefined && Beats.eq(k.t, t)) return id;
  }
  return undefined;
}

function applyEase(tx: TxBuilder, key: KeyId, ease: Ease): void {
  const current = tx.doc.keys.get(key)?.ease;
  if (ease.kind === 'hold') {
    if (current?.kind !== 'hold') Core.keys.toggleHold(tx, { segments: [key] });
  } else {
    Core.keys.setEase(tx, { segments: [key], ease });
  }
}

/** Writes keyframes `points` (in time order) on a line property, creating the track when needed. */
function animate(tx: TxBuilder, line: LineId, prop: LineProp, layer: number, points: readonly Point[]): void {
  const first = points[0];
  if (first === undefined) return;
  const track =
    tx.idx.track(line, prop, layer) ??
    Core.keys.ensureTrack(tx, { owner: { kind: 'line', line, prop, layer }, value: first.v, at: beat(first.t) });
  let prev: { readonly id: KeyId; readonly ease: Ease } | null = null;
  for (const p of points) {
    const t = beat(p.t);
    const id = keyAt(tx, track, t) ?? Core.keys.insertKey(tx, { track, t, value: p.v }).key;
    if (prev !== null) applyEase(tx, prev.id, prev.ease);
    Core.keys.setKeyValues(tx, { keys: [id], part: 'both', value: p.v });
    prev = { id, ease: p.ease ?? E.linear };
  }
  if (prev !== null && tx.idx.nextKey(prev.id) !== undefined) applyEase(tx, prev.id, prev.ease);
}

/** Position path (X and Y share their beats and curves: new lines have X / Y linked). */
function animatePos(
  tx: TxBuilder,
  line: LineId,
  points: ReadonlyArray<{ readonly t: number; readonly x: number; readonly y: number; readonly ease?: Ease }>,
): void {
  animate(
    tx,
    line,
    'moveX',
    0,
    points.map((p) => ({ t: p.t, v: p.x, ...(p.ease === undefined ? {} : { ease: p.ease }) })),
  );
  animate(
    tx,
    line,
    'moveY',
    0,
    points.map((p) => ({ t: p.t, v: p.y, ...(p.ease === undefined ? {} : { ease: p.ease }) })),
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------------------------------------------

type NoteSpec = readonly [t: number, kind: NoteKind, x: number, end?: number];

/** Notes of Main (section by section; holds carry their end beat). */
const MAIN_NOTES: readonly NoteSpec[] = [
  // Intro (bars 2-4).
  [4, 'tap', -300],
  [5, 'tap', -100],
  [6, 'tap', 100],
  [7, 'tap', 300],
  [8, 'hold', 0, 10],
  [10, 'tap', -200],
  [11, 'tap', 200],
  [12, 'flick', -200],
  [13, 'drag', -150],
  [13.5, 'drag', -50],
  [14, 'drag', 50],
  [14.5, 'drag', 150],
  [15, 'flick', 200],
  // Groove (bars 5-8), answered by Partner.
  [16, 'tap', -250],
  [18, 'tap', 250],
  [20, 'tap', -250],
  [22, 'tap', 250],
  [24, 'hold', -250, 26],
  [26, 'drag', -300],
  [26.25, 'drag', -200],
  [26.5, 'drag', -100],
  [26.75, 'drag', 0],
  [27, 'drag', 100],
  [27.25, 'drag', 200],
  [27.5, 'drag', 300],
  [28, 'flick', 0],
  [29, 'tap', -150],
  [30, 'tap', 150],
  [31, 'tap', 0],
  // Lift (bars 9-12): sparse while the line spins.
  [32, 'tap', 0],
  [34, 'tap', -200],
  [36, 'tap', 200],
  [38, 'hold', 0, 40],
  [40, 'drag', -300],
  [40.5, 'drag', -150],
  [41, 'drag', 0],
  [41.5, 'drag', 150],
  [42, 'drag', 300],
  [44, 'tap', -300],
  [45, 'tap', -100],
  [46, 'tap', 100],
  [47, 'tap', 300],
  // Drive (bars 13-16).
  [48, 'tap', -200],
  [49, 'tap', 200],
  [50, 'tap', -200],
  [51, 'tap', 200],
  [52, 'flick', -300],
  [53, 'tap', 0],
  [54, 'flick', 300],
  [55, 'tap', 0],
  [56, 'hold', -200, 58],
  [56, 'hold', 200, 58],
  [59, 'tap', 0],
  [60, 'flick', -250],
  [61, 'tap', 0],
  [62, 'flick', 250],
  [63, 'tap', 0],
];

const PARTNER_NOTES: readonly NoteSpec[] = [
  [17, 'tap', 250],
  [19, 'tap', -250],
  [21, 'tap', 250],
  [23, 'tap', -250],
  [24, 'hold', 250, 26],
  [28, 'flick', 0],
];

function drafts(line: LineId, specs: readonly NoteSpec[]): NoteDraft[] {
  return specs.map(([t, kind, x, end]) => ({
    line,
    kind,
    t: beat(t),
    x,
    ...(kind === 'hold' && end !== undefined ? { end: beat(end) } : {}),
  }));
}

// ---------------------------------------------------------------------------------------------------------------
// The chart
// ---------------------------------------------------------------------------------------------------------------

const WHITE: RGB = [255, 255, 255];
const WARM: RGB = [255, 206, 140];

/** Builds the chart's lines, motions and notes (Core commands only). */
function buildChart(
  tx: TxBuilder,
  text: SampleText,
  assets: { readonly texture: AssetId; readonly gif: AssetId },
): void {
  const L = text.lines;
  const main = Core.lines.addLine(tx, { preset: 'standard', name: L.main });
  const partner = Core.lines.addLine(tx, { preset: 'standard', name: L.partner, parent: main, at: [0, 400] });
  const title = Core.lines.addLine(tx, { preset: 'text', name: L.title, at: [0, 330], text: '' });
  const picture = Core.lines.addLine(tx, {
    preset: 'image',
    name: L.picture,
    texture: assets.texture,
    at: [-480, 170],
  });
  const spinner = Core.lines.addLine(tx, { preset: 'gif', name: L.spinner, texture: assets.gif, at: [480, 170] });
  const brush = Core.lines.addLine(tx, { preset: 'paint', name: L.brush, at: [-500, -60] });
  const score = Core.lines.addLine(tx, { preset: 'uiMover', name: L.score, attachUI: 'score' });

  // Main: swing left and right, spin, a wobble on a second (additive) rotation layer, fade out.
  animatePos(tx, main, [
    { t: 0, x: 0, y: -250, ease: E.hold },
    { t: 16, x: 0, y: -250, ease: E.outSine },
    { t: 20, x: -300, y: -250, ease: E.inOutSine },
    { t: 24, x: 300, y: -250, ease: E.inSine },
    { t: 28, x: 0, y: -250, ease: E.hold },
  ]);
  animate(tx, main, 'rotate', 0, [
    { t: 0, v: 0, ease: E.hold },
    { t: 32, v: 0, ease: E.inOutCubic },
    { t: 36, v: 360, ease: E.hold },
  ]);
  const wobble = Core.keys.addLayer(tx, { line: main, prop: 'rotate' });
  animate(tx, main, 'rotate', wobble, [
    { t: 48, v: 0, ease: E.inOutSine },
    { t: 50, v: 8, ease: E.inOutSine },
    { t: 52, v: -8, ease: E.inOutSine },
    { t: 54, v: 0, ease: E.hold },
  ]);
  animate(tx, main, 'alpha', 0, [
    { t: 0, v: 255, ease: E.hold },
    { t: 60, v: 255 },
    { t: 64, v: 0, ease: E.hold },
  ]);

  // Partner: hidden until the groove, then fades in.
  animate(tx, partner, 'alpha', 0, [
    { t: 0, v: 0, ease: E.hold },
    { t: 14, v: 0, ease: E.outQuad },
    { t: 16, v: 255, ease: E.hold },
    { t: 32, v: 255, ease: E.inQuad },
    { t: 34, v: 0, ease: E.hold },
  ]);

  // Title: type the first words on, switch to the second ones, warm up the colour, fade out.
  const [first, second] = text.words;
  animate(tx, title, 'text', 0, [
    { t: 0, v: '', ease: E.linear },
    { t: 4, v: first, ease: E.hold },
    { t: 12, v: second, ease: E.hold },
  ]);
  const textTrack = tx.idx.track(title, 'text', 0);
  const typeOn = textTrack === undefined ? undefined : tx.idx.keysByTrack(textTrack)[0];
  if (typeOn !== undefined) Core.lines.setTextSegmentMode(tx, { segment: typeOn, mode: 'typeOn', to: first });
  animate(tx, title, 'color', 0, [
    { t: 0, v: WHITE, ease: E.hold },
    { t: 12, v: WHITE, ease: E.inOutSine },
    { t: 16, v: WARM, ease: E.hold },
  ]);
  animate(tx, title, 'alpha', 0, [
    { t: 0, v: 255, ease: E.hold },
    { t: 28, v: 255, ease: E.inQuad },
    { t: 32, v: 0, ease: E.hold },
  ]);

  // Picture: fade in, turn slowly, fade out.
  animate(tx, picture, 'alpha', 0, [
    { t: 0, v: 0, ease: E.hold },
    { t: 8, v: 0, ease: E.outQuad },
    { t: 12, v: 255, ease: E.hold },
    { t: 44, v: 255, ease: E.inQuad },
    { t: 48, v: 0, ease: E.hold },
  ]);
  animate(tx, picture, 'rotate', 0, [
    { t: 0, v: 0, ease: E.hold },
    { t: 16, v: 0, ease: E.linear },
    { t: 48, v: 360, ease: E.hold },
  ]);

  // Spinner: fade in, bob up and down.
  animate(tx, spinner, 'alpha', 0, [
    { t: 0, v: 0, ease: E.hold },
    { t: 8, v: 0, ease: E.linear },
    { t: 10, v: 255, ease: E.hold },
    { t: 56, v: 255, ease: E.linear },
    { t: 60, v: 0, ease: E.hold },
  ]);
  animatePos(tx, spinner, [
    { t: 0, x: 480, y: 170, ease: E.hold },
    { t: 32, x: 480, y: 170, ease: E.inOutSine },
    { t: 36, x: 480, y: 230, ease: E.inOutSine },
    { t: 40, x: 480, y: 170, ease: E.hold },
  ]);

  // Brush: appears in the drive and paints a zigzag.
  animate(tx, brush, 'alpha', 0, [
    { t: 0, v: 0, ease: E.hold },
    { t: 48, v: 255, ease: E.hold },
    { t: 58, v: 255, ease: E.linear },
    { t: 60, v: 0, ease: E.hold },
  ]);
  animatePos(tx, brush, [
    { t: 0, x: -500, y: -60, ease: E.hold },
    { t: 48, x: -500, y: -60, ease: E.inOutSine },
    { t: 50, x: -250, y: 100, ease: E.inOutSine },
    { t: 52, x: 0, y: -60, ease: E.inOutSine },
    { t: 54, x: 250, y: 100, ease: E.inOutSine },
    { t: 56, x: 500, y: -60, ease: E.hold },
  ]);

  // Score mover: nudges the score aside and back.
  animatePos(tx, score, [
    { t: 0, x: 0, y: 0, ease: E.hold },
    { t: 56, x: 0, y: 0, ease: E.outBack },
    { t: 58, x: -120, y: -40, ease: E.inOutSine },
    { t: 60, x: 0, y: 0, ease: E.hold },
  ]);

  const template = Defaults.noteTemplate();
  Core.notes.addNotes(tx, { drafts: [...drafts(main, MAIN_NOTES), ...drafts(partner, PARTNER_NOTES)], template });
}

async function sha256(bytes: Uint8Array): Promise<AssetId> {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  const digest = await crypto.subtle.digest('SHA-256', copy);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('') as AssetId;
}

async function asset(bytes: Uint8Array, path: string, role: AssetRole, extra: AssetMeta = {}): Promise<SampleAsset> {
  const sniff = sniffMedia(bytes, path);
  return {
    id: await sha256(bytes),
    bytes,
    mime: sniff.mime,
    role,
    meta: { ...assetMetaOf(bytes, sniff), ...extra },
    path,
  };
}

/** Generates the media and builds the sample project's document (see the module header). */
export async function buildSampleProject(
  text: SampleText,
  opts: { readonly createContext?: OfflineContextFactory; readonly rpeId?: string } = {},
): Promise<SampleProject> {
  const song = await sampleSongWav(opts.createContext === undefined ? {} : { createContext: opts.createContext });
  const music = await asset(song.bytes, SAMPLE_PATHS.music, 'music', {
    durationSec: song.durationSec,
    sampleRate: song.sampleRate,
    channels: song.channels,
  });
  const illustration = await asset(sampleIllustration().bytes, SAMPLE_PATHS.illustration, 'illustration');
  const texture = await asset(sampleTexture().bytes, SAMPLE_PATHS.texture, 'texture');
  const gif = await asset(sampleGif().bytes, SAMPLE_PATHS.gif, 'gif');
  const assets = [music, illustration, texture, gif];

  const previewStart = Math.round(Math.min(45, Math.max(0, song.durationSec - 16)) * 1000) / 1000;
  const previewEnd = Math.round(Math.min(previewStart + 15, song.durationSec - 0.5) * 1000) / 1000;

  const empty = Defaults.emptyDoc({ name: text.title, bpm: SAMPLE_BPM, offsetMs: 0, rpeId: opts.rpeId ?? newRpeId() });
  const { doc } = buildDocWithCore(empty, (tx) => {
    for (const a of assets) {
      Core.doc.addAsset(tx, {
        ref: { id: a.id, path: a.path, mime: a.mime, bytes: a.bytes.length, role: a.role, meta: a.meta },
      });
    }
    Core.doc.patchMeta(tx, {
      patch: {
        composer: text.credit,
        charter: text.credit,
        illustrator: text.credit,
        level: 'EZ Lv.2',
        difficulty: 2,
        previewStart,
        previewEnd,
        music: music.id,
        illustration: illustration.id,
        tags: ['plain'],
        intro: text.intro,
      },
    });
    Core.tempo.setTimingConfirmed(tx, { confirmed: true });
    buildChart(tx, text, { texture: texture.id, gif: gif.id });
  });
  return { doc, assets, durationSec: song.durationSec };
}
